// src/pages/BuyerProfileSetupPage.js
import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Alert, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Chip, Box } from '@mui/material';
import { http } from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import { geocodeAddress } from '../lib/geocode';
import { ART_TYPE_OPTIONS } from '../utils/hostProfileOptions';
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import { getAgreementStatus } from "../lib/apiAgreements";
import ContextHelpButton from '../components/ContextHelpButton';

const BUYER_AGREEMENT_CONFIG = {
  agreementCode: "BUYER_PLATFORM",
  title: "Buyer Membership Agreement",
  workflowContext: { workflow: "buyer_onboarding" },
};

export default function BuyerProfileSetupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ address: '', art_preferences: [] });
  const [error, setError] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleArtPreferencesChange = (e) => {
    const { value } = e.target;
    setForm({ ...form, art_preferences: typeof value === 'string' ? value.split(',') : value });
  };

  const getCoordinatesFromAddress = async (address) => {
    const data = await geocodeAddress(address);
    if (data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry;
      return { latitude: lat, longitude: lng };
    }
    throw new Error('Address not found');
  };

  const getCurrentUserId = async () => {
    const { data } = await http.get('/api/accounts/me/');
    return data.id;
  };
  
  const fetchExistingBuyer = async (userId) => {
    const buyerRes = await http.get(`/buyers/?user=${userId}`);
    console.log('existing buyer: ', buyerRes);
    return buyerRes.data.length > 0 ? buyerRes.data[0] : null;
  };

  const getBuyerAgreementStatus = async () => {
    try {
      const res = await getAgreementStatus([BUYER_AGREEMENT_CONFIG.agreementCode]);
      const row = res.results?.find(item => item.code === BUYER_AGREEMENT_CONFIG.agreementCode);
      if (!row) return { exists: false, accepted: true };
      return { exists: true, accepted: !!row.accepted };
    } catch (error) {
      console.error('Failed to check buyer agreement status:', error);
      return { exists: false, accepted: true };
    }
  };

  const handleFinalSubmit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // 1) who is the user?
      const userId = await getCurrentUserId();

      // 2) prevent duplicate profile
      const existingBuyer = await fetchExistingBuyer(userId);
      if (existingBuyer) {
        alert('You already have a Buyer profile. Redirecting you to your dashboard.');
        navigate('/dashboard/buyer');
        return;
      }

      // 3) geocode
      const coords = await getCoordinatesFromAddress(form.address);

      // 4) create including required user field
      await http.post('/buyers/', {
        user: { id: userId },
        address: form.address,
        location: coords,
        art_preferences: form.art_preferences,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/dashboard/buyer');
    } catch (err) {
      console.error('Failed to create buyer profile:', err.response?.data || err.message);
      setError('Failed to create buyer profile.');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const agreementStatus = await getBuyerAgreementStatus();
    if (agreementStatus.exists && !agreementStatus.accepted) {
      setPendingSubmit(true);
      setShowAgreementModal(true);
      return;
    }
    await handleFinalSubmit();
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="h5">Set Up Your Buyer Profile</Typography>
        <ContextHelpButton section="buyer-help" title="Help for buyer profile setup" />
      </Box>
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField fullWidth label="Address" name="address" onChange={handleChange} margin="normal" required />
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Art Preferences</InputLabel>
          <Select
            multiple
            name="art_preferences"
            value={form.art_preferences}
            onChange={handleArtPreferencesChange}
            input={<OutlinedInput label="Art Preferences" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((val) => (<Chip key={val} label={val} />))}
              </Box>
            )}
          >
            {ART_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="contained" sx={{ mt: 2 }}>Save Profile</Button>
      </form>
      <AgreementAcceptModal
        open={showAgreementModal}
        onClose={() => {
          setShowAgreementModal(false);
          setPendingSubmit(false);
        }}
        agreementCode={BUYER_AGREEMENT_CONFIG.agreementCode}
        title={BUYER_AGREEMENT_CONFIG.title}
        workflowContext={BUYER_AGREEMENT_CONFIG.workflowContext}
        onAccepted={async () => {
          setShowAgreementModal(false);
          if (pendingSubmit) {
            setPendingSubmit(false);
            await handleFinalSubmit();
          }
        }}
      />
    </Container>
  );
}
